// FASE 2 punt 5 — Interne notities per orderregel (Orders Decoprof).
// Notities zijn ALLEEN intern zichtbaar — niet in pakbon, niet in klant-mails.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface OrderLineNote {
  id: string;
  orderid: number;
  ordernumber: string | null;
  lineitemindex: number | null;
  sku: string | null;
  note: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const ORDER_NOTES_KEY = (orderid: number) => ["order-line-notes", orderid] as const;
const HAS_NOTES_KEY = ["order-line-notes:has"] as const;

/** Fetch notities voor één order (laat bij expand laden). */
export const useOrderLineNotes = (orderid: number | null) => {
  const { isAuthenticated, isSessionValid } = useAuth();
  return useQuery({
    queryKey: orderid != null ? ORDER_NOTES_KEY(orderid) : ["order-line-notes", "none"],
    enabled: !!orderid && isAuthenticated && isSessionValid,
    staleTime: 30_000,
    queryFn: async (): Promise<OrderLineNote[]> => {
      const { data, error } = await supabase
        .from("order_line_notes" as any)
        .select("*")
        .eq("orderid", orderid as number)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as OrderLineNote[];
    },
  });
};

/** Lichte set van orderids die ≥1 notitie hebben — voor het note-icoontje op de rij. */
export const useOrderIdsWithNotes = () => {
  const { isAuthenticated, isSessionValid } = useAuth();
  return useQuery({
    queryKey: HAS_NOTES_KEY,
    enabled: isAuthenticated && isSessionValid,
    staleTime: 60_000,
    queryFn: async (): Promise<Set<number>> => {
      const { data, error } = await supabase
        .from("order_line_notes" as any)
        .select("orderid")
        .limit(5000);
      if (error) throw error;
      const set = new Set<number>();
      for (const r of (data ?? []) as any[]) {
        if (typeof r.orderid === "number") set.add(r.orderid);
      }
      return set;
    },
  });
};

interface SaveArgs {
  orderid: number;
  ordernumber: string | null;
  lineitemindex: number | null;
  sku: string | null;
  note: string;
  createdBy: string | null;
  existingId?: string | null;
}

export const useSaveOrderLineNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: SaveArgs): Promise<OrderLineNote> => {
      const payload = {
        orderid: args.orderid,
        ordernumber: args.ordernumber,
        lineitemindex: args.lineitemindex,
        sku: args.sku,
        note: args.note,
        created_by: args.createdBy,
      };
      if (args.existingId) {
        const { data, error } = await supabase
          .from("order_line_notes" as any)
          .update({ note: args.note })
          .eq("id", args.existingId)
          .select("*")
          .single();
        if (error) throw error;
        return data as unknown as OrderLineNote;
      }
      const { data, error } = await supabase
        .from("order_line_notes" as any)
        .insert(payload)
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as OrderLineNote;
    },
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: ORDER_NOTES_KEY(note.orderid) });
      qc.invalidateQueries({ queryKey: HAS_NOTES_KEY });
    },
  });
};

export const useDeleteOrderLineNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; orderid: number }) => {
      const { error } = await supabase
        .from("order_line_notes" as any)
        .delete()
        .eq("id", args.id);
      if (error) throw error;
      return args;
    },
    onSuccess: (args) => {
      qc.invalidateQueries({ queryKey: ORDER_NOTES_KEY(args.orderid) });
      qc.invalidateQueries({ queryKey: HAS_NOTES_KEY });
    },
  });
};
